
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useGuitarMatch } from '../context/GuitarMatchContext';
import { SideNavigation } from './SideNavigation';
import { MemberHub } from './auth/MemberHub';
import { TeacherOverlay } from './auth/TeacherOverlay';
import { StemImport } from './StemImport';
import { SubscriptionOverlay } from './SubscriptionOverlay';
import { HelpHub } from './support/HelpHub';
import { TutorialOverlay } from './TutorialOverlay';
import { TeacherVideo, AppState, RecordingMetadata, MatchType, HelpTab, YouTubeReference } from '../types';
import { GodModePanel } from './admin/GodModePanel';
import { SkillAssessment } from './assessment/SkillAssessment';
import { ProductTruthPanel } from './debug/ProductTruthPanel';
import { ScoringGuide } from './ScoringGuide';
import { APP_CONFIG } from '../services/config';

interface LayoutManagerProps {
    children: React.ReactNode;
    onSongSelect: (title: string, grid: string[]) => void;
    onTeacherSelect: (video: TeacherVideo) => void;
    onStemImport: (fileOrRef: File | YouTubeReference, bpm: number, grid: string, offset?: number) => void;
    startAnalysis: (file: File | YouTubeReference, metadata?: RecordingMetadata, qualityWarning?: string, matchTypeOverride?: MatchType) => Promise<void>;
}

export const LayoutManager: React.FC<LayoutManagerProps> = ({ 
    children, onSongSelect, onTeacherSelect, onStemImport, startAnalysis
}) => {
    const { 
        appState, userProfile, lang,
        activeOverlay, setActiveOverlay,
        isMenuOpen, setIsMenuOpen,
        hubTab, setHubTab,
        helpTab, setHelpTab,
        showSubscriptionOffer,
        showSupportHub, setShowSupportHub,
        showTutorial, setShowTutorial,
        setMatchType, setManualGrid, setTargetBpm
    } = useGuitarMatch();

    useEffect(() => {
        const root = document.documentElement;
        root.style.setProperty('--smart-accent', APP_CONFIG.TENANT.theme.accent);
        root.style.setProperty('--smart-base', APP_CONFIG.TENANT.theme.base);
    }, []);

    const handleStartAssessment = useCallback(() => {
        setMatchType('ASSESSMENT');
        setManualGrid("G C | D | G");
        setTargetBpm(90);
        localStorage.setItem('gsc_is_assessment', 'true');
        setActiveOverlay('SKILL_TEST');
    }, [setMatchType, setManualGrid, setTargetBpm, setActiveOverlay]);

    const handleStemImport = (fileOrRef: File | YouTubeReference, bpm: number, grid: string, offset?: number) => {
        onStemImport(fileOrRef, bpm, grid, offset);
        setActiveOverlay('NONE'); // FIX: Fermer l'overlay après l'import
    };

    const showHeader = appState !== AppState.IDLE;

    return (
        <div className="relative w-full h-screen text-white overflow-hidden font-sans select-none">
            
            {/* 1. OBSIDIAN MULTI-LAYER BG */}
            <div className="obsidian-bg"></div>
            
            {/* 2. DYNAMIC LIGHT LEAKS */}
            <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden opacity-40">
                <div className="absolute top-[-20%] left-[-10%] w-[80%] h-[80%] bg-cyan-600/10 blur-[160px] rounded-full animate-float-slow"></div>
                <div className="absolute bottom-[-20%] right-[-10%] w-[80%] h-[80%] bg-smart-accent/10 blur-[180px] rounded-full animate-float-delayed"></div>
            </div>

            <div className="relative z-10 w-full h-full flex flex-col">
                {showHeader && (
                    <div id="app-header" className="absolute top-0 left-0 right-0 p-8 flex justify-between items-start pointer-events-none z-50">
                        <button 
                            id="btn-menu"
                            onClick={() => setIsMenuOpen(true)} 
                            className="pointer-events-auto w-16 h-16 bg-white/[0.03] backdrop-blur-3xl rounded-3xl flex flex-col items-center justify-center gap-1.5 border border-white/10 hover:bg-white hover:text-black transition-all group shadow-2xl"
                        >
                            <span className="w-6 h-[2px] bg-current"></span>
                            <span className="w-4 h-[2px] bg-current"></span>
                            <span className="w-6 h-[2px] bg-current"></span>
                        </button>
                        
                        <div className="pointer-events-auto flex items-center gap-4">
                            <div className="bg-black/40 px-4 py-2 rounded-full border border-white/10 flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
                                <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Neural Online</span>
                            </div>
                        </div>
                    </div>
                )}

                <div className="flex-1 w-full h-full overflow-hidden">
                    {children}
                </div>
            </div>

            {/* OVERLAYS */}
            {isMenuOpen && (
                <SideNavigation 
                    onClose={() => setIsMenuOpen(false)} 
                    onOpenHub={(tab) => { setHelpTab(tab as HelpTab); setShowSupportHub(true); setIsMenuOpen(false); }}
                    onOpenSupport={() => { setHelpTab('HISTORY'); setShowSupportHub(true); setIsMenuOpen(false); }}
                    onOpenTeacher={() => { setActiveOverlay('TEACHER'); setIsMenuOpen(false); }}
                    onStartTutorial={() => { setShowTutorial(true); setIsMenuOpen(false); }}
                    onStartAssessment={() => { handleStartAssessment(); setIsMenuOpen(false); }}
                    onOpenGodMode={() => { setActiveOverlay('GOD_MODE'); setIsMenuOpen(false); }}
                />
            )}

            {activeOverlay === 'HUB' && (
                <MemberHub 
                    member={userProfile as any} 
                    initialTab={hubTab === 'INSTITUTION' ? 'INSTITUTION' : 'SONGBOOK'} 
                    onSelectSong={onSongSelect} 
                    onClose={() => setActiveOverlay('NONE')} 
                    lang={lang} 
                />
            )}
            {activeOverlay === 'TEACHER' && (
                <TeacherOverlay onSelectTeacherVideo={onTeacherSelect} onClose={() => setActiveOverlay('NONE')} lang={lang} />
            )}
            {activeOverlay === 'STEM' && (
                <div className="fixed inset-0 z-[20000] bg-black/90 backdrop-blur-3xl">
                    <StemImport onImport={handleStemImport} onCancel={() => setActiveOverlay('NONE')} lang={lang} />
                </div>
            )}
            {activeOverlay === 'GOD_MODE' && <GodModePanel onClose={() => setActiveOverlay('NONE')} />}
            {activeOverlay === 'TRUTH_PANEL' && <ProductTruthPanel onClose={() => setActiveOverlay('NONE')} />}
            {activeOverlay === 'SKILL_TEST' && (
                <SkillAssessment onCancel={() => { setActiveOverlay('NONE'); }} onComplete={() => { setActiveOverlay('NONE'); }} startAnalysisOverride={async (f, m, q, mt) => startAnalysis(f, m, q, mt)} />
            )}
            {activeOverlay === 'SCORING_GUIDE' && (
                <ScoringGuide isOpen={true} onClose={() => setActiveOverlay('NONE')} lang={lang} />
            )}

            {showSubscriptionOffer && <SubscriptionOverlay />}
            {showSupportHub && <HelpHub initialTab={helpTab} />}
            {showTutorial && <TutorialOverlay isOpen={showTutorial} onClose={() => setShowTutorial(false)} />}
        </div>
    );
};
